// Popular email providers we check against for likely typos (Mailcheck-style).
const POPULAR_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "aol.com",
  "live.com",
  "msn.com",
  "comcast.net",
  "protonmail.com",
]

export interface EmailIssue {
  message: string
  suggestion?: string
}

function levenshtein(a: string, b: string): number {
  const dp: number[][] = Array.from({length: a.length + 1}, () => new Array(b.length + 1).fill(0))
  for (let i = 0; i <= a.length; i++) dp[i][0] = i
  for (let j = 0; j <= b.length; j++) dp[0][j] = j
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1])
    }
  }
  return dp[a.length][b.length]
}

// Returns null if the email looks fine, or an EmailIssue describing what
// looks wrong (plus a suggested fix, when we're confident enough to propose
// one) so the caller can pause and ask the user to confirm before it's used.
export function checkEmailForIssues(rawEmail: string): EmailIssue | null {
  const email = rawEmail.trim().toLowerCase()
  const atIndex = email.lastIndexOf("@")
  if (atIndex < 1 || atIndex === email.length - 1) return null

  const localPart = email.slice(0, atIndex)
  const domain = email.slice(atIndex + 1)
  const labels = domain.split(".")

  if (labels.length < 2 || labels.some((label) => label.length === 0)) {
    return {message: `"${rawEmail.trim()}" doesn't look like a complete email address. Please double-check it.`}
  }

  const tld = labels[labels.length - 1]
  // The TLDs people actually type for ticket purchases (.com, .net, .org,
  // .io, .co, .us, ...) top out at 3 characters. Anything longer, or
  // anything with non-letters, usually means stray text got glued onto the
  // address by accident (e.g. "gmail.comasdasdasd" or "gmail.com674715").
  if (!/^[a-z]{2,3}$/.test(tld)) {
    return {message: `"${rawEmail.trim()}" looks off — please double-check it before we send your ticket there.`}
  }

  const secondLevelDomain = labels.slice(-2).join(".")
  let bestMatch: {domain: string; distance: number} | null = null
  for (const known of POPULAR_DOMAINS) {
    if (secondLevelDomain === known) {
      bestMatch = null
      break
    }
    const distance = levenshtein(secondLevelDomain, known)
    if (distance <= 2 && (!bestMatch || distance < bestMatch.distance)) {
      bestMatch = {domain: known, distance}
    }
  }

  if (bestMatch) {
    return {
      message: `Did you mean ${localPart}@${bestMatch.domain}?`,
      suggestion: `${localPart}@${bestMatch.domain}`,
    }
  }

  return null
}
